// Privacy by country (super admins): record which legal gates are met in each
// country, and switch personal-data features on there. A feature can be
// switched on only once every gate for that country is recorded; the gateway
// enforces the same rule (maps/privacyProfiles.ts). Every change is audit-logged.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { privacyProfilesApi, getErrorMessage, type PrivacyProfile } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { checkbox } from '@/components/ui/fieldStyles';

const COUNTRY: Record<string, string> = { NG: 'Nigeria', GH: 'Ghana', KE: 'Kenya', ZA: 'South Africa', EG: 'Egypt', MA: 'Morocco', RW: 'Rwanda' };
const FEATURE_LABEL: Record<string, string> = {
  accounts: 'Accounts', history: 'Trip history', probes: 'Speed sharing', photo_contributions: 'Photo contributions', edge_imagery: 'Street imagery',
};
const label = (s: string) => s.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()).replace(/\bDpo\b/, 'DPO').replace(/\bDpia\b/, 'DPIA').replace(/\bDcpmi\b/, 'DCPMI')
  .replace(/\bOdpc\b/, 'ODPC').replace(/\bNcsa\b/, 'NCSA').replace(/\bPdpc\b/, 'PDPC').replace(/\bCndp\b/, 'CNDP');

function CountryCard({ p, features }: { p: PrivacyProfile; features: string[] }) {
  const qc = useQueryClient();
  const [gates, setGates] = useState<string[]>(p.gates_met);
  const [on, setOn] = useState<string[]>(p.features_enabled);
  const save = useMutation({
    mutationFn: () => privacyProfilesApi.update(p.country_code, gates, on),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['privacy-profiles'] }),
  });
  const allMet = p.gates_required.every(g => gates.includes(g));
  const dirty = JSON.stringify([...gates].sort()) !== JSON.stringify([...p.gates_met].sort()) || JSON.stringify([...on].sort()) !== JSON.stringify([...p.features_enabled].sort());
  const toggle = (list: string[], set: (v: string[]) => void, v: string) => set(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);

  return (
    <Panel
      title={<span>{COUNTRY[p.country_code] ?? p.country_code} <span className="font-mono text-xs text-muted-foreground">{p.country_code}</span></span>}
      action={<span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${p.features_enabled.length ? 'bg-success/15 text-success' : 'bg-surface-raised text-muted-foreground'}`}>
        {p.features_enabled.length ? `${p.features_enabled.length} on` : 'Nothing on'}</span>}>
      <p className="text-xs text-muted-foreground">{p.law} · {p.regulator}</p>
      <fieldset className="mt-4">
        <legend className="text-[13px] font-medium text-foreground">Gates met</legend>
        <div className="mt-2 space-y-1.5">
          {p.gates_required.map(g => (
            <label key={g} className="flex items-center gap-2 text-sm text-foreground">
              <input type="checkbox" className={checkbox} checked={gates.includes(g)}
                onChange={() => { toggle(gates, setGates, g); if (gates.includes(g)) setOn([]); }} />
              {label(g)}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="mt-4">
        <legend className="text-[13px] font-medium text-foreground">Features</legend>
        {!allMet && <p className="mt-1 text-xs text-muted-foreground">Record every gate first.</p>}
        <div className="mt-2 flex flex-wrap gap-2">
          {features.map(f => (
            <label key={f} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[13px] ${on.includes(f) ? 'border-accent bg-accent/10 text-foreground' : 'border-border text-muted-foreground'} ${!allMet ? 'opacity-50' : ''}`}>
              <input type="checkbox" className="sr-only" disabled={!allMet} checked={on.includes(f)} onChange={() => toggle(on, setOn, f)} />
              {FEATURE_LABEL[f] ?? label(f)}
            </label>
          ))}
        </div>
      </fieldset>
      {!!save.error && <p role="alert" className="mt-3 text-sm text-error">{getErrorMessage(save.error)}</p>}
      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">{p.approved_at ? `Approved ${new Date(p.approved_at * 1000).toLocaleDateString('en-GB')}` : 'Not approved'}</p>
        <button type="button" disabled={!dirty || save.isPending} onClick={() => save.mutate()}
          className="inline-flex h-9 items-center rounded-full bg-accent px-4 text-[13px] font-medium text-[#0B0C0E] disabled:opacity-50">
          {save.isPending ? 'Saving' : 'Save'}
        </button>
      </div>
    </Panel>
  );
}

export default function PrivacyProfilesPage() {
  const { data, isLoading, error } = useQuery({ queryKey: ['privacy-profiles'], queryFn: () => privacyProfilesApi.list().then(r => r.data) });
  return (
    <div className="space-y-6">
      <PageHeader title="Privacy by country"
        description="Latlens offers a personal-data feature in a country only once that country's legal gates are met. Record each gate when it is done, with counsel's sign-off, then switch the feature on. Changes are audit-logged." />
      {!!error && <p role="alert" className="rounded-xl border border-error/30 bg-error/10 p-3 text-sm text-error">{getErrorMessage(error)}</p>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {isLoading
          ? [0, 1, 2, 3].map(i => <div key={i} className="skeleton h-64 rounded-2xl" aria-hidden="true" />)
          : data?.profiles.map(p => <CountryCard key={`${p.country_code}-${p.updated_at}`} p={p} features={data.features} />)}
      </div>
    </div>
  );
}
